import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useGlobalStore } from './store';
import { eventBus } from './event-bus';

// 创建 axios 实例
const http: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
http.interceptors.request.use(
  (config) => {
    const { token } = useGlobalStore.getState();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
http.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    const { response } = error;
    
    if (response) {
      switch (response.status) {
        case 401:
          // Token 过期或无效
          useGlobalStore.getState().logout();
          eventBus.emit('toast:show', {
            message: '登录已过期，请重新登录',
            type: 'error',
          });
          // 跳转到登录页
          window.location.href = '/login';
          break;
        case 403:
          eventBus.emit('toast:show', {
            message: '没有权限执行此操作',
            type: 'error',
          });
          break;
        case 404:
          eventBus.emit('toast:show', {
            message: '请求的资源不存在',
            type: 'error',
          });
          break;
        case 500:
          eventBus.emit('toast:show', {
            message: '服务器错误，请稍后重试',
            type: 'error',
          });
          break;
        default:
          eventBus.emit('toast:show', {
            message: response.data?.message || '请求失败',
            type: 'error',
          });
      }
    } else {
      eventBus.emit('toast:show', {
        message: '网络连接失败，请检查网络',
        type: 'error',
      });
    }
    
    return Promise.reject(error);
  }
);

// API 响应类型
export interface ApiResponse<T = unknown> {
  code: number;
  message: string;
  data: T;
}

// 分页响应类型
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// 封装请求方法
export const api = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    http.get(url, config).then((res) => res.data),

  post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    http.post(url, data, config).then((res) => res.data),

  put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    http.put(url, data, config).then((res) => res.data),

  patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    http.patch(url, data, config).then((res) => res.data),

  delete: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> =>
    http.delete(url, config).then((res) => res.data),
};

export default http;

